import { useEffect, useState } from "react";
import { adminApi, adminUsageApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HelpButton from "@/components/HelpButton";
import { Crown, Users, Buildings, Scroll, ChartBar, EnvelopeSimple, Plus, TrashSimple, Cpu, Lifebuoy } from "@phosphor-icons/react";
import { toast } from "sonner";
import SupportPanel from "@/components/SupportPanel";

export default function Admin() {
    const { user: me } = useAuth();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [chambers, setChambers] = useState([]);
    const [audit, setAudit] = useState([]);
    const [allowlist, setAllowlist] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [newNote, setNewNote] = useState("");

    const load = async () => {
        try {
            const [s, u, c, a, al] = await Promise.all([
                adminApi.stats(),
                adminApi.users(),
                adminApi.chambers(),
                adminApi.audit(200),
                adminApi.listAllowlist(),
            ]);
            setStats(s); setUsers(u); setChambers(c); setAudit(a); setAllowlist(al);
        } catch (e) {
            toast.error("Failed to load admin data");
        }
    };

    useEffect(() => { load(); }, []);

    const toggleRole = async (u) => {
        const next = u.role === "admin" ? "user" : "admin";
        if (!window.confirm(`Set ${u.email} role to ${next}?`)) return;
        await adminApi.setRole(u.user_id, next);
        await load();
    };

    const addEmail = async () => {
        if (!newEmail.trim()) return;
        try {
            await adminApi.addAllowlist(newEmail.trim().toLowerCase(), newNote.trim());
            setNewEmail("");
            setNewNote("");
            toast.success("Email approved");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add");
        }
    };

    const removeEmail = async (id) => {
        if (!window.confirm("Remove this email from allowlist?")) return;
        await adminApi.removeAllowlist(id);
        await load();
    };

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="admin-page">
            <div className="rule-line pt-3 mb-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">
                    <Crown size={12} weight="fill" /> Superuser
                </div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Admin Console</h1>
                    <HelpButton topic="admin" testid="help-admin" />
                </div>
            </div>

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
                    {Object.entries(stats).map(([k, v]) => (
                        <Card key={k} data-testid={`stat-${k}`}>
                            <CardContent className="p-3">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k.replace(/_/g, " ")}</div>
                                <div className="font-serif text-3xl">{v}</div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Tabs defaultValue="users">
                <TabsList>
                    <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={14} className="mr-1" /> Users ({users.length})</TabsTrigger>
                    <TabsTrigger value="allowlist" data-testid="admin-tab-allowlist"><EnvelopeSimple size={14} className="mr-1" /> Allowlist ({allowlist.length})</TabsTrigger>
                    <TabsTrigger value="chambers" data-testid="admin-tab-chambers"><Buildings size={14} className="mr-1" /> Chambers ({chambers.length})</TabsTrigger>
                    <TabsTrigger value="llm_usage" data-testid="admin-tab-llm-usage"><Cpu size={14} className="mr-1" /> LLM costs</TabsTrigger>
                    <TabsTrigger value="audit" data-testid="admin-tab-audit"><Scroll size={14} className="mr-1" /> Audit log</TabsTrigger>
                    {me?.role === "superadmin" && (
                        <TabsTrigger value="support" data-testid="admin-tab-support"><Lifebuoy size={14} className="mr-1" /> Support</TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="users" className="mt-4">
                    <div className="border border-border rounded-md bg-white overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted text-muted-foreground">
                                <tr>
                                    <th className="text-left p-3">Email</th>
                                    <th className="text-left p-3">Name</th>
                                    <th className="text-left p-3">Role</th>
                                    <th className="text-left p-3">Last login</th>
                                    <th className="text-right p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.user_id} className="border-t border-border" data-testid={`admin-user-${u.user_id}`}>
                                        <td className="p-3">{u.email}</td>
                                        <td className="p-3">{u.name || "—"}</td>
                                        <td className="p-3"><span className={`chip ${u.role === "superadmin" ? "chip-ai" : u.role === "admin" ? "chip-external" : "chip-doc"}`}>{u.role || "user"}</span></td>
                                        <td className="p-3 text-xs text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</td>
                                        <td className="p-3 text-right">
                                            {u.role === "superadmin" ? (
                                                <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Crown size={12} weight="fill" /> Superadmin</span>
                                            ) : (
                                                <Button size="sm" variant="outline" onClick={() => toggleRole(u)}>
                                                    {u.role === "admin" ? "Demote" : "Make admin"}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="allowlist" className="mt-4" data-testid="allowlist-panel">
                    <div className="border border-border rounded-md bg-white p-5 mb-4">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Approve sign-in</div>
                        <p className="text-sm text-muted-foreground mb-3 max-w-xl">
                            Add an email to allow that person to sign in with Google. Emails not on this list, not invited to a chamber, and not a global admin will be rejected at login.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                                type="email"
                                placeholder="advocate@example.com"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                className="sm:w-64"
                                data-testid="allowlist-email-input"
                            />
                            <Input
                                placeholder="note (optional, e.g., 'Senior associate')"
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                className="flex-1"
                                data-testid="allowlist-note-input"
                            />
                            <Button onClick={addEmail} data-testid="allowlist-add-btn"><Plus size={14} /> Approve</Button>
                        </div>
                    </div>
                    <div className="border border-border rounded-md bg-white overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted text-muted-foreground">
                                <tr>
                                    <th className="text-left p-3">Email</th>
                                    <th className="text-left p-3">Note</th>
                                    <th className="text-left p-3">Approved by</th>
                                    <th className="text-left p-3">When</th>
                                    <th className="text-right p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!allowlist.length ? (
                                    <tr><td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">No approved emails yet. Admins listed in <code className="font-mono text-[11px]">ADMIN_EMAILS</code> can sign in by default.</td></tr>
                                ) : allowlist.map((a) => (
                                    <tr key={a.id} className="border-t border-border" data-testid={`allowlist-row-${a.id}`}>
                                        <td className="p-3 font-mono text-xs">{a.email}</td>
                                        <td className="p-3 text-xs text-muted-foreground">{a.note || "—"}</td>
                                        <td className="p-3 text-xs">{a.added_by_email}</td>
                                        <td className="p-3 text-xs text-muted-foreground">{new Date(a.added_at).toLocaleString()}</td>
                                        <td className="p-3 text-right">
                                            <button className="text-muted-foreground hover:text-destructive" onClick={() => removeEmail(a.id)} data-testid={`allowlist-remove-${a.id}`}>
                                                <TrashSimple size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="chambers" className="mt-4">
                    {!chambers.length ? <div className="text-sm text-muted-foreground">No chambers yet.</div> : (
                        <div className="grid md:grid-cols-2 gap-3">
                            {chambers.map((c) => (
                                <div key={c.id} className="border border-border rounded-md bg-white p-4">
                                    <div className="font-serif text-lg">{c.name}</div>
                                    <div className="text-xs text-muted-foreground">{c.owner_email}</div>
                                    <div className="text-xs mt-2">{c.member_count} members</div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="llm_usage" className="mt-4" data-testid="llm-usage-panel">
                    <LLMUsagePanel />
                </TabsContent>

                {me?.role === "superadmin" && (
                    <TabsContent value="support" className="mt-4">
                        <SupportPanel />
                    </TabsContent>
                )}

                <TabsContent value="audit" className="mt-4">
                    <div className="border border-border rounded-md bg-white overflow-hidden">
                        <div className="max-h-[60vh] overflow-y-auto scrollbar-slim">
                            <table className="w-full text-sm">
                                <thead className="bg-muted text-muted-foreground sticky top-0">
                                    <tr>
                                        <th className="text-left p-2 font-mono text-xs">When</th>
                                        <th className="text-left p-2">User</th>
                                        <th className="text-left p-2">Action</th>
                                        <th className="text-left p-2">Meta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {audit.map((a) => (
                                        <tr key={a.id} className="border-t border-border">
                                            <td className="p-2 font-mono text-[11px] text-muted-foreground whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                                            <td className="p-2 font-mono text-[11px]">{a.user_id?.slice(0, 18)}</td>
                                            <td className="p-2"><span className="chip chip-external">{a.action}</span></td>
                                            <td className="p-2 text-xs text-muted-foreground font-mono truncate max-w-md">{JSON.stringify(a.meta || {}).slice(0, 200)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

// ============== LLM cost reporting ==============

function fmtINR(n) { return "₹ " + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtUSD(n) { return "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }); }
function fmtNum(n) { return Number(n || 0).toLocaleString("en-IN"); }

function LLMUsagePanel() {
    const [period, setPeriod] = useState("week");
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [series, setSeries] = useState([]);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [s, d, r] = await Promise.all([
                adminUsageApi.summary(period, "all"),
                adminUsageApi.daily(days, "all"),
                adminUsageApi.recent({ limit: 30, scope: "all" }),
            ]);
            setSummary(s); setSeries(d.series || []); setRecent(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load usage data");
        }
        setLoading(false);
    };
    useEffect(() => { load(); }, [period, days]); // eslint-disable-line react-hooks/exhaustive-deps

    const maxBar = Math.max(0.0001, ...series.map((s) => s.cost_inr || 0));

    if (loading && !summary) return <div className="p-6 text-sm text-muted-foreground">Loading LLM cost report…</div>;

    return (
        <div className="space-y-5">
            <div className="flex justify-between flex-wrap gap-2 items-center">
                <div className="text-sm text-muted-foreground">
                    Pulled from <code className="font-mono">db.llm_usage_log</code> — every <code className="font-mono">run_llm</code> call is logged.
                    {summary?.usd_to_inr_fx && <> Conversion at <b>1 USD = ₹{summary.usd_to_inr_fx}</b>.</>}
                </div>
                <div className="flex gap-2">
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-[160px]" data-testid="usage-period"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="today">Today</SelectItem>
                            <SelectItem value="week">Last 7 days</SelectItem>
                            <SelectItem value="month">Last 30 days</SelectItem>
                            <SelectItem value="quarter">Last 90 days</SelectItem>
                            <SelectItem value="fy">This FY (Apr–Mar)</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                        <SelectTrigger className="w-[150px]" data-testid="usage-chart-days"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">Chart: 7 days</SelectItem>
                            <SelectItem value="30">Chart: 30 days</SelectItem>
                            <SelectItem value="90">Chart: 90 days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Headline KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Total spend (INR)" value={fmtINR(summary?.totals?.cost_inr)} sub={`${summary?.totals?.calls || 0} calls`} tone="emerald" testid="usage-kpi-inr" />
                <KpiCard label="Total spend (USD)" value={fmtUSD(summary?.totals?.cost_usd)} sub={`avg ${summary?.totals?.avg_duration_ms || 0} ms`} testid="usage-kpi-usd" />
                <KpiCard label="Tokens processed" value={fmtNum(summary?.totals?.total_tokens)} sub={`${fmtNum(summary?.totals?.input_tokens)} in · ${fmtNum(summary?.totals?.output_tokens)} out`} testid="usage-kpi-tokens" />
                <KpiCard label="Reliability" value={`${summary?.totals?.calls ? Math.round((summary.totals.success_calls / summary.totals.calls) * 100) : 0}%`} sub={`${summary?.totals?.error_calls || 0} fails · ${summary?.totals?.timeout_calls || 0} timeouts · ${summary?.totals?.fallback_calls || 0} fallbacks`} tone={(summary?.totals?.error_calls || 0) > 0 ? "amber" : "emerald"} testid="usage-kpi-rel" />
            </div>

            {/* Daily cost chart */}
            <div className="border border-border bg-white rounded-md p-4" data-testid="usage-daily-chart">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Daily cost (₹) — last {days} days</div>
                <div className="overflow-x-auto">
                    <div className="flex items-end gap-0.5 min-w-[600px] h-[150px]">
                        {series.map((s) => {
                            const pct = ((s.cost_inr || 0) / maxBar) * 100;
                            // Give non-zero bars a minimum visible height so 1-paisa days don't look like zero
                            const height = s.cost_inr > 0 ? Math.max(pct, 2) : 0;
                            return (
                                <div key={s.date} className="flex-1 flex flex-col items-center" title={`${s.date}: ${fmtINR(s.cost_inr)} · ${s.calls} calls · ${fmtNum(s.total_tokens)} tokens`}>
                                    <div className="w-full bg-primary/40 rounded-t" style={{ height: `${height}%` }} />
                                </div>
                            );
                        })}
                    </div>
                </div>
                {series.length > 0 && (
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                        <span>{series[0].date}</span>
                        <span>{series[series.length - 1].date}</span>
                    </div>
                )}
            </div>

            {/* Breakdown grids */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <BreakdownTable title="By feature" rows={summary?.by_feature || []} keyField="feature" testid="usage-by-feature" />
                <BreakdownTable title="By model" rows={summary?.by_model || []} keyField={(r) => `${r.provider}/${r.model}`} testid="usage-by-model" />
                <BreakdownTable title="By user" rows={(summary?.by_user || []).map((u) => ({ ...u, label: u.name }))} keyField="label" testid="usage-by-user" />
            </div>

            {/* Recent calls */}
            <div className="border border-border bg-white rounded-md overflow-hidden" data-testid="usage-recent">
                <div className="bg-muted p-2 px-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Recent calls</div>
                <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-white border-b border-border text-muted-foreground sticky top-0">
                            <tr>
                                <th className="text-left p-2">Time</th>
                                <th className="text-left p-2">Feature</th>
                                <th className="text-left p-2">Provider / Model</th>
                                <th className="text-left p-2">Status</th>
                                <th className="text-right p-2">Tokens</th>
                                <th className="text-right p-2">Cost</th>
                                <th className="text-right p-2">Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.map((r) => (
                                <tr key={r.id} className="border-t border-border">
                                    <td className="p-2 font-mono text-[10px] text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                                    <td className="p-2">{r.feature}{r.fallback_used && <span className="chip chip-external ml-1 text-[9px]">fallback</span>}</td>
                                    <td className="p-2 font-mono text-[10px]">{r.provider}/{r.model}</td>
                                    <td className="p-2"><span className={`chip text-[10px] ${r.status === "success" ? "chip-ai" : r.status === "timeout" ? "chip-external" : "chip-doc"}`}>{r.status}</span></td>
                                    <td className="p-2 text-right font-mono">{fmtNum(r.total_tokens)}</td>
                                    <td className="p-2 text-right font-mono">{fmtINR(r.cost_inr)}</td>
                                    <td className="p-2 text-right font-mono text-muted-foreground">{Math.round(r.duration_ms)} ms</td>
                                </tr>
                            ))}
                            {!recent.length && (
                                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No LLM calls logged yet. Trigger an AI feature (assess, suggest, refine, etc) to populate.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function KpiCard({ label, value, sub, tone = "default", testid }) {
    const ring = {
        emerald: "border-emerald-300 bg-emerald-50/40",
        amber: "border-amber-300 bg-amber-50/40",
        destructive: "border-destructive/40 bg-destructive/5",
        default: "border-border bg-white",
    }[tone];
    return (
        <div className={`border rounded-md p-3 ${ring}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            <div className="font-serif text-2xl mt-1">{value}</div>
            {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
        </div>
    );
}

function BreakdownTable({ title, rows, keyField, testid }) {
    return (
        <div className="border border-border bg-white rounded-md overflow-hidden" data-testid={testid}>
            <div className="bg-muted p-2 px-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{title}</div>
            <div className="max-h-[280px] overflow-y-auto">
                <table className="w-full text-xs">
                    <thead className="text-muted-foreground border-b border-border">
                        <tr>
                            <th className="text-left p-2">Key</th>
                            <th className="text-right p-2">Calls</th>
                            <th className="text-right p-2">Cost (INR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!rows.length && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">—</td></tr>}
                        {rows.map((r) => {
                            const key = typeof keyField === "function" ? keyField(r) : (r[keyField] ?? "—");
                            return (
                                <tr key={key} className="border-t border-border">
                                    <td className="p-2 truncate" title={key}>{key}</td>
                                    <td className="p-2 text-right font-mono">{r.calls}</td>
                                    <td className="p-2 text-right font-mono">{fmtINR(r.cost_inr)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
